import React, { useState } from 'react';
import { FaTimes, FaBullseye, FaUsers, FaChartLine, FaCrown } from 'react-icons/fa';

const ChangeObjectiveModal = ({ isOpen, onClose, onObjectiveChange, objectiveTypes, currentObjective }) => {
    const [selectedType, setSelectedType] = useState('');
    const [loading, setLoading] = useState(false);

    const objectiveTypeConfig = {
        INCREASE_SALES: {
            icon: <FaChartLine className="text-green-500" />,
            title: 'Aumentar Ventas',
            description: 'Optimiza tus estrategias de marketing para incrementar las ventas de tu negocio. Incluye análisis de datos, mejora de conversiones y optimización de campañas.',
            color: 'bg-gradient-to-r from-green-500 to-teal-600',
            defaultName: 'Aumentar Ventas',
            standardDescription: 'Plan estratégico enfocado en el crecimiento de ventas a través de marketing digital, optimización de conversiones y análisis de datos para tomar mejores decisiones comerciales.'
        },
        ACQUIRE_NEW_CUSTOMERS: {
            icon: <FaUsers className="text-blue-500" />,
            title: 'Conseguir Nuevos Clientes',
            description: 'Expande tu base de clientes con estrategias de captación efectivas. Incluye marketing digital, programas de referidos y campañas de alcance.',
            color: 'bg-gradient-to-r from-blue-500 to-indigo-600',
            defaultName: 'Conseguir Nuevos Clientes',
            standardDescription: 'Estrategia integral para atraer nuevos clientes mediante marketing digital, programas de referidos y acciones específicas para aumentar la visibilidad de tu negocio.'
        },
        RETAIN_EXISTING_CUSTOMERS: {
            icon: <FaCrown className="text-purple-500" />,
            title: 'Fidelizar Clientes Existentes',
            description: 'Fortalece la relación con tus clientes actuales. Incluye programas de fidelización, comunicación personalizada y mejora de la experiencia del cliente.',
            color: 'bg-gradient-to-r from-purple-500 to-pink-600',
            defaultName: 'Fidelizar Clientes Existentes',
            standardDescription: 'Programa completo de fidelización enfocado en mantener y mejorar la relación con tus clientes actuales a través de estrategias de retención y mejora de la experiencia.'
        }
    };

    const handleTypeSelect = (type) => {
        setSelectedType(type);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedType) return;

        setLoading(true);
        try {
            const config = objectiveTypeConfig[selectedType];
            const objectiveData = {
                objectiveType: selectedType,
                objectiveName: config.defaultName,
                objectiveDescription: config.standardDescription
            };

            await onObjectiveChange(objectiveData);
            onClose();
        } catch (error) {
            console.error('Error al cambiar objetivo:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedType('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-900">
                        Cambiar Objetivo Principal
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FaTimes className="text-xl" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Current Objective */}
                    {currentObjective && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                Objetivo Actual
                            </h3>
                            <div className={`p-4 rounded-lg text-white ${objectiveTypeConfig[currentObjective.objectiveType]?.color || 'bg-gray-500'}`}>
                                <div className="flex items-center mb-2">
                                    {objectiveTypeConfig[currentObjective.objectiveType]?.icon}
                                    <span className="ml-2 font-semibold">
                                        {currentObjective.objectiveName}
                                    </span>
                                </div>
                                <p className="text-white/90 text-sm">
                                    {objectiveTypeConfig[currentObjective.objectiveType]?.standardDescription || currentObjective.objectiveDescription}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Objective Types */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                            Selecciona un nuevo objetivo
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Object.entries(objectiveTypeConfig).map(([type, config]) => (
                                <div
                                    key={type}
                                    onClick={() => handleTypeSelect(type)}
                                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-lg ${selectedType === type
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center mb-2">
                                        <div className="text-2xl mr-3">
                                            {config.icon}
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900">
                                                {config.title}
                                            </h4>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        {config.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {selectedType && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <h4 className="font-semibold text-blue-900 mb-2">💡 Sobre este objetivo</h4>
                            <p className="text-sm text-blue-800">
                                {objectiveTypeConfig[selectedType].standardDescription}
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || !selectedType}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Cambiando...' : 'Cambiar Objetivo'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangeObjectiveModal; 